import React from 'react';

export type BooleanState = {
    value: boolean;
    setValue: (expanded: boolean) => void;
    toggle: () => void;
    setTrue: () => void;
    setFalse: () => void;
};

export type BooleanStateOptions = {
    initialState?: boolean;
    onToggle?: (expanded: boolean) => void;
    isControlledState?: boolean;
};

export const stateHooks = {
  useBooleanState(options?: BooleanStateOptions): BooleanState {
    const {
      initialState,
      onToggle,
      isControlledState,
    } = options || {};
    const [expanded, setValue] = React.useState(!!initialState);
    const [isUpdated, setIsUpdated] = React.useState(true);

    React.useEffect(() => {
      if (isControlledState && isUpdated) {
        setValue(!!initialState)
        if (initialState && onToggle) {
          onToggle(true)
        }
      }
    }, [initialState, isControlledState, isUpdated, onToggle])

    const setTrue = React.useCallback(() => {
      setValue(true);
      setIsUpdated(false)
      if (onToggle) {
        onToggle(true);
      }
    }, [setValue, onToggle]);

    const setFalse = React.useCallback(() => {
      setValue(false);
      setIsUpdated(false)
      if (onToggle) {
        onToggle(false);
      }
    }, [setValue, onToggle]);

    const toggle = React.useCallback(() => {
      setValue(!expanded);
      setIsUpdated(false)
      if (onToggle) {
        onToggle(!expanded);
      }
    }, [expanded, setValue, onToggle]);

    return {
      value: expanded,
      setValue,
      toggle,
      setTrue,
      setFalse,
    };
  },
  useControlledState<T>(controlledState: T) {
    const [state, setState] = React.useState<T>(controlledState);

    React.useEffect(() => {
      setState(controlledState);
    }, [controlledState]);

    return [state, setState] as const;
  },
  useTimeoutExpired(enabled: boolean, timeoutMs: number) {
    const [expired, setExpired] = React.useState(false);
    React.useEffect(() => {
      let timeout: number;
      if (enabled) {
        timeout = window.setTimeout(() => {
          setExpired(true);
        }, timeoutMs);
      }

      return () => window.clearTimeout(timeout);
    }, [enabled, timeoutMs]);

    return expired;
  },
};